import * as React from "react";
import {
  Box,
  Paper,
  Popper,
  Fade,
  Typography,
  useTheme,
  Divider,
} from "@mui/material";
import { tokens } from "../../theme";

export type FocusPopupPlacement =
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end";

export interface FocusPopupItem {
  label: string;
  value: React.ReactNode;
}

export interface FieldFocusPopupProps {
  children: React.ReactNode;
  enabled?: boolean;
  title?: string;
  icon?: React.ReactNode;
  items?: FocusPopupItem[];
  content?: React.ReactNode;
  placement?: FocusPopupPlacement;
  width?: number;
  offset?: [number, number];
}

const FieldFocusPopup: React.FC<FieldFocusPopupProps> = ({
  children,
  enabled = false,
  title,
  icon,
  items,
  content,
  placement = "right-start",
  width = 230,
  offset = [0, 10],
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [fieldFocused, setFieldFocused] = React.useState(false);
  const blurTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (!enabled || fieldFocused) return;
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 130);
  };

  const handleMouseDown = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(false);
  };

  const handleFocus = () => {
    setFieldFocused(true);
    if (blurTimer.current) clearTimeout(blurTimer.current);
    setOpen(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only unfocus if focus leaves the container entirely
    if (!anchorRef.current?.contains(e.relatedTarget as Node)) {
      setFieldFocused(false);
    }
  };

  React.useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );
  const popupBg = isDark ? "rgba(28, 38, 50, 0.95)" : "#ffffff";

  const border = isDark
    ? "1px solid rgba(255,255,255,0.08)"
    : `1px solid ${colors.grey[700]}`;

  const shadow = isDark
    ? "0 0 0 1px rgba(255,255,255,0.04), 0 4px 6px -1px rgba(0,0,0,0.4), 0 16px 32px -4px rgba(0,0,0,0.2)"
    : "0 0 0 1px rgba(0,0,0,0.03), 0 4px 6px -1px rgba(0,0,0,0.07), 0 12px 28px -4px rgba(0,0,0,0.10)";

  const headerBorder = isDark
    ? "1px solid rgba(255,255,255,0.06)"
    : `1px solid ${colors.grey[700]}`;

  const iconBg = isDark ? "rgba(0,167,111,0.15)" : "#F0FBF7";

  const titleColor = isDark ? "#E8ECF0" : colors.grey[100];
  const labelColor = isDark ? colors.grey[600] : "#8A96A3";
  const valueColor = isDark ? "#D0D8E4" : colors.grey[100];
  const hintColor = isDark ? colors.grey[400] : "#454F5B";
  const divColor = isDark ? "rgba(255,255,255,0.05)" : colors.grey[700];

  const arrowBorderColor = isDark ? "rgba(255,255,255,0.08)" : colors.grey[700];

  const getArrowSx = (pl: string) => {
    const base = {
      position: "absolute" as const,
      width: 10,
      height: 10,
      background: popupBg,
      transform: "rotate(45deg)",
      zIndex: 2,
    };
    if (pl.startsWith("right"))
      return {
        ...base,
        left: -5,
        top: 20,
        borderBottom: `1px solid ${arrowBorderColor}`,
        borderLeft: `1px solid ${arrowBorderColor}`,
      };
    if (pl.startsWith("left"))
      return {
        ...base,
        right: -5,
        top: 20,
        borderTop: `1px solid ${arrowBorderColor}`,
        borderRight: `1px solid ${arrowBorderColor}`,
      };
    if (pl.startsWith("bottom"))
      return {
        ...base,
        top: -5,
        left: 20,
        borderTop: `1px solid ${arrowBorderColor}`,
        borderLeft: `1px solid ${arrowBorderColor}`,
      };
    return {
      ...base,
      bottom: -5,
      left: 20,
      borderBottom: `1px solid ${arrowBorderColor}`,
      borderRight: `1px solid ${arrowBorderColor}`,
    };
  };

  if (!enabled) return <>{children}</>;

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{ position: "relative", display: "block", width: "100%" }}
    >
      {children}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        transition
        style={{ zIndex: 1400 }}
        modifiers={[
          { name: "offset", options: { offset } },
          {
            name: "preventOverflow",
            options: { boundary: "viewport", padding: 8 },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["left-start", "bottom-start", "top-start"],
            },
          },
        ]}
      >
        {({ TransitionProps, placement: activePlacement }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Box sx={{ position: "relative" }}>
            <Paper
              elevation={0}
              sx={{
                width,
                border,
                borderRadius: "12px",
                overflow: "hidden",
                background: popupBg,
                boxShadow: shadow,
                backdropFilter: isDark ? "blur(16px)" : "none",
                WebkitBackdropFilter: isDark ? "blur(16px)" : "none",
              }}
            >
              {title && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.625,
                    py: 1.25,
                    borderBottom: headerBorder,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: titleColor,
                      letterSpacing: "0.01em",
                      lineHeight: 1,
                    }}
                  >
                    {title}
                  </Typography>

                  {icon && (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "6px",
                        background: iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        "& svg": { width: 11, height: 11 },
                      }}
                    >
                      {icon}
                    </Box>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  px: 1.625,
                  pt: title ? 1.25 : 1.5,
                  pb: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.875,
                }}
              >
                {items &&
                  items.length > 0 &&
                  items.map((item, idx) => (
                    <React.Fragment key={idx}>
                      {item.value === null ? (
                        <Divider sx={{ borderColor: divColor, my: 0.25 }} />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: labelColor,
                              fontWeight: 400,
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                              lineHeight: 1.5,
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              color: valueColor,
                              textAlign: "right",
                              lineHeight: 1.5,
                            }}
                          >
                            {item.value}
                          </Box>
                        </Box>
                      )}
                    </React.Fragment>
                  ))}

                {content && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: hintColor,
                      lineHeight: 1.65,
                    }}
                  >
                    {content}
                  </Typography>
                )}
              </Box>
            </Paper>
              <Box sx={getArrowSx(activePlacement)} />
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default FieldFocusPopup;

//How to use FieldFocusPopup---------------------
//  PLACEMENT OPTIONS
//  ─────────────────
//  right-start* │ right │ right-end
//  left-start   │ left  │ left-end
//  bottom-start │ bottom│ bottom-end
//  top-start    │ top   │ top-end
//  (* default)
//
//  EXAMPLES
//  ────────
//  // Dropdown with hint paragraph
//  <FieldFocusPopup enabled title="Practice" content="Select the billing practice.">
//    <CustomDropdownField name="practiceId" control={control} options={opts} />
//  </FieldFocusPopup>
//
//  // Divider between row groups (pass value: null)
//  <FieldFocusPopup enabled title="Date of birth"
//    items={[
//      { label: "Format",  value: "MM / DD / YYYY" },
//      { label: "—",       value: null },            // ← divider
//      { label: "Min age", value: "Must be 18+" },
//    ]}
//  >
//    <CustomDatePicker controllerName="dob" control={control} label="DOB" />
//  </FieldFocusPopup>

//  // Conditional — show for specific roles only
//  <FieldFocusPopup enabled={user.role === "admin"} title="Notes" content="Admin only.">
//    <CustomTextField controllerName="notes" control={control} label="Notes" />
//  </FieldFocusPopup>
